import { Direction2D, distanceEquals, Line2D, LineSegment2D, Polygon2D } from '@paragon/geometry-library';

export const HATCH_SPACING = 6; // inches - gap between stripes
export const STRIPE_SPACING = 1; // inches - spacing between lines within a stripe

/** The number of lines in each stripe. */
export type HatchingMode = number;

/**
 * Generates hatching lines within a rectangular polygon based on the hatching mode.
 * Lines are oriented relative to the polygon's primary (long) axis direction.
 */
export function generateEnvelopeHatchLines(polygon: Polygon2D, stripeCount: HatchingMode): LineSegment2D[] {
  const sortedEdges = polygon.edges.toSorted((a, b) => b.length - a.length);
  const [primaryEdge, secondLongestEdge] = sortedEdges;

  const isSquare =
    polygon.edges.length === 4 && polygon.edges.every((edge) => distanceEquals(edge.length, primaryEdge.length));
  if (!isSquare && !primaryEdge.direction.isParallelTo(secondLongestEdge.direction)) {
    throw new Error(
      'Malformed beam or wall polygon -- expected two longest edges to be parallel. Bearing envelopes must be rectangular.',
    );
  }

  return generateAngledHatchLines(polygon, primaryEdge.direction, stripeCount, 45);
}

/**
 * Generates `stripeCount` hatching lines within a `polygon` at `angle` degrees relative to the `primaryDirection`.
 *
 * If stripeCount > 1, the lines in each stripe will be spaced `STRIPE_SPACING` apart, and separated by `HATCH_SPACING`
 * (measured as gap between end of one stripe and start of next).
 */
function generateAngledHatchLines(
  polygon: Polygon2D,
  primaryDirection: Direction2D,
  stripeCount: number,
  angle: number,
): LineSegment2D[] {
  const stripeWidth = (stripeCount - 1) * STRIPE_SPACING;
  const hatchDirection = primaryDirection.rotate(angle);
  const perpDirection = hatchDirection.rotateQuarterTurn();
  const [minProjection, maxProjection] = polygon.projectionBoundsFromCentroid(perpDirection);

  const segments: LineSegment2D[] = [];

  for (let stripeStart = minProjection; stripeStart <= maxProjection; stripeStart += stripeWidth + HATCH_SPACING) {
    for (let i = 0; i < stripeCount; i++) {
      const offset = stripeStart + i * STRIPE_SPACING;
      if (offset > maxProjection) break;

      const lineBasePoint = polygon.centroid.translate(perpDirection.toVector(offset));
      const scanLine = new Line2D(lineBasePoint, hatchDirection);
      segments.push(...polygon.findSegmentIntersectionsWithLine(scanLine));
    }
  }

  return segments;
}
